import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ConversationRow as ConversationRowData } from '../../../generated/api/chat-conversations'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useAuth } from '../../../hooks/useAuth'
import { useHamburger } from '../../../hooks/useHamburgerContext'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useSelectedChatId } from '../../../state/context/SelectedChatIdProvider'
import { formatRelative } from '../../../helpers/date'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { theme } from '../../../theme/theme'
import { AvatarInner } from '../../common/Avatar'
import { NotificationBadge } from '../../notifications/NotificationBadge'

interface ConversationRowProps {
  row: ConversationRowData
  onClick?: (row: ConversationRowData) => void
  className?: string
}

const AVATAR_PLACEHOLDER = {
  user: 'avatar',
  interests: 'interests',
  society: 'society',
} as const

export const ConversationRow = ({
  row,
  onClick,
  className,
}: ConversationRowProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const navigate = useNavigate()
  const { selectedChatRoomId, setSelectedChatRoomId } = useSelectedChatId()
  const { setDisplaySidebar } = useHamburger()
  const isMobile = useIsMobile()

  const selected = selectedChatRoomId === row.id
  const title =
    row.type === 'oneonone' && !row.title
      ? translate('common.user.deletedUserName')
      : row.title
  const { lastMessage, unreadCount } = row

  const getLastMessageString = (): string | null => {
    if (!lastMessage) return null

    const isOwnMessage = lastMessage.authorId === userId
    const authorName = lastMessage.authorFirstName || title

    if (!lastMessage.text) {
      if (!lastMessage.hasMedia) return null
      return isOwnMessage
        ? translate('chatRoomsSidebar.message.userSentImageText')
        : translate('chatRoomsSidebar.message.otherUserSentImageText', {
            name: authorName,
          })
    }

    if (isOwnMessage) {
      return `${translate('common.prefixes.you')}: ${lastMessage.text}`
    }
    if (row.type !== 'oneonone' && lastMessage.authorFirstName) {
      return `${lastMessage.authorFirstName}: ${lastMessage.text}`
    }
    return lastMessage.text
  }

  const lastMessageString = getLastMessageString()

  const onRowClick = (): void => {
    setSelectedChatRoomId(row.id)
    if (isMobile) setDisplaySidebar(false)
    onClick?.(row)
  }

  const onAvatarClick = row.otherUserId
    ? (e: React.MouseEvent | React.KeyboardEvent): void => {
        e.preventDefault()
        e.stopPropagation()
        navigate(
          reverseUrl('user:society-detail', {
            id: row.otherUserId,
            societyId: row.societyId,
          })
        )
      }
    : undefined

  return (
    <Link
      className={`flex justify-between gap-4 break-all hover:bg-neutral-96
        ${className ?? ''}
        ${selected ? 'bg-gray-light' : ''}
      `}
      onClick={onRowClick}
      to={reverseUrl('chat:detail', { id: row.id })}
    >
      <div
        role="presentation"
        className={onAvatarClick ? 'cursor-pointer hover:brightness-90' : ''}
        onClick={onAvatarClick}
        onKeyDown={(e) => e.key === 'Enter' && onAvatarClick?.(e)}
      >
        <AvatarInner
          alt={`${title} avatar`}
          url={row.avatar.url}
          initials={row.avatar.initials}
          placeholder={AVATAR_PLACEHOLDER[row.avatar.kind]}
        />
      </div>
      <div className="flex flex-1 flex-col">
        <p style={{ ...theme.textVariants.baseBold }} className="line-clamp-1">
          {title}
        </p>
        {lastMessageString && (
          <p
            style={
              unreadCount
                ? theme.textVariants.captionBold
                : theme.textVariants.caption
            }
            className="line-clamp-1"
          >
            {lastMessageString}
          </p>
        )}
      </div>
      <div className="flex flex-col">
        <p
          style={{ ...theme.textVariants.fineprint }}
          className="line-clamp-1 text-neutral-50"
        >
          {formatRelative(row.lastActivityAt)}
        </p>
        {unreadCount > 0 && (
          <div className="flex h-full items-center justify-end">
            <NotificationBadge show absoluteOffset={false} />
          </div>
        )}
      </div>
    </Link>
  )
}
